import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { fetchNewsById, NewsArticle } from "./NewsAPIUtil";
import { useTranslations } from "src/contexts/TranslationContext";
import TwitterImageCardMeta from 'src/components/TwitterImageCardMeta';
import ReactMarkdown from 'react-markdown';
// import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SocialShareButtons from './SocialShareButtons';
import { addStructuredData, renderMedia, processFootnotes, components, useNewsHreflangTags, getLanguageFromPath, getPathPrefix, useCurrentPath } from './NewsDetailsHelper';

// interface NewsDetailParams {
//     lang: string;
//     id: string;
//   }
  
  const NewsDetail: React.FC = () => {
    const { lang, id } = useParams<{ [key: string]: string | undefined }>();
    if (!lang || !id) {
        return <div>Error: Missing language or news id in URL</div>;
      }



    const location = useLocation();
    const navigate = useNavigate();

    // Use translations context
    const { translations: t, language: contextLanguage } = useTranslations();

    const [news, setNews] = useState<NewsArticle | null>(null);
    const [loading, setLoading] = useState(true);
    const [embedKey, setEmbedKey] = useState(0);
    const [showSource, setShowSource] = useState(false);
    const [lastLanguage, setLastLanguage] = useState<string | null>(null);

    const { updateHreflangTags } = useNewsHreflangTags(news?.avail_langs || []);

    // Extract type and subtype from URL path
    const getTypeAndSubtypeFromPath = useCallback(() => {
        const pathParts = location.pathname.split('/');
        // For the main news page, we want to return 'news' as the type
        if (pathParts.includes('news')) {
            return {
                type: 'news',
                subtype: null
            };
        }
        // For other types
        const typeIndex = pathParts.findIndex(part =>
            ['Science', 'Sports', 'Entertainment']
                .includes(part)
        );
        if (typeIndex !== -1) {
            return {
                type: pathParts[typeIndex],
                subtype: pathParts[typeIndex + 1]
            };
        }
        return { type: null, subtype: null };
    }, [location.pathname]);



    // Use the language from URL path first, then props, then context
    const getCurrentLanguage = useCallback(() => {
        const pathLanguage = getLanguageFromPath();
        console.log(`[NewsDetail] Language from path: ${pathLanguage}, prop lang: ${lang}, context language: ${contextLanguage}`);
        return lang || pathLanguage || contextLanguage || 'en';
    }, [lang, contextLanguage, getLanguageFromPath]);

    // Get translated type name
    const getTypeTranslation = (type: string | undefined) => {
        if (!type) return '';
        return t?.navigations?.header?.[type.toLowerCase()] || type;
    };

    // Get translated subtype name
    const getSubtypeTranslation = (subtype: string) => {
        if (t?.navigations?.header?.[subtype]) {
            return t.navigations.header[subtype];
        }
        return subtype.charAt(0).toUpperCase() + subtype.slice(1); // Fallback to capitalized subtype
    };

    // Debug language changes
    useEffect(() => {
        console.log(`[NewsDetail] Path changed to: ${location.pathname}, detected language: ${getLanguageFromPath()}`);
    }, [location.pathname, getLanguageFromPath]);

    // Function to load news with a specific language
    const loadNews = useCallback(async (newsId: string, newsLang: string) => {
        if (!newsId) return;

        console.log(`[NewsDetail] Loading news with ID ${newsId} in language ${newsLang}`);
        setLoading(true);
        try {
            const newsData = await fetchNewsById(newsId, newsLang);

            // Clean the data by removing footnote references
            if (newsData) {
                // Remove footnote references from title
                if (newsData.title) {
                    newsData.title = newsData.title.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, '');
                }

                // Remove footnote references from news content
                if (newsData.news) {
                    newsData.news = newsData.news.replace(/\[\^?\d+\](?:\[\^?\d+\])*/g, '');
                }

                console.log("[NewsDetail] Cleaned news data of footnote references");
            }

            setNews(newsData);
            setLastLanguage(newsLang);
            if (newsData) {
                console.log("[NewsDetail] Adding structured data for news:", newsData);
                addStructuredData(newsData);
            }
        } catch (error) {
            console.error("[NewsDetail] Error loading news:", error);
        } finally {
            setLoading(false);
            setEmbedKey(prev => prev + 1);
        }
    }, []);

    // Force reload when path changes to ensure proper language
    useEffect(() => {
        const currentLang = getCurrentLanguage();
        // Always update UI immediately when language changes
        if (currentLang !== lastLanguage) {
            console.log(`[NewsDetail] Language changed from ${lastLanguage} to ${currentLang}, reloading content`);

            // Set loading immediately for better UX
            setLoading(true);

            // Set current language as last language immediately to avoid flicker
            setLastLanguage(currentLang);

            // Reload news data if we have an ID
            if (id) {
                loadNews(id, currentLang);
            }
        }
    }, [location.pathname, id, getCurrentLanguage, lastLanguage, loadNews]);

    // Initial load of news
    useEffect(() => {
        if (id && !lastLanguage) {
            const currentLang = getCurrentLanguage();
            loadNews(id, currentLang);
        }
    }, [id, lastLanguage, getCurrentLanguage, loadNews]);

    useEffect(() => {
        // Remove any existing Twitter widgets.js script to avoid duplicates
        const existingScript = document.querySelector('script[src="https://platform.twitter.com/widgets.js"]');
        if (existingScript) {
            existingScript.remove();
        }

        // Function to add the script
        const addScript = () => {
            const script = document.createElement("script");
            script.src = "https://platform.twitter.com/widgets.js";
            script.async = true;

            script.onload = () => {
                if (window.twttr?.widgets) {
                    window.twttr.widgets.load();
                }
            };

            document.body.appendChild(script);
        };

        // Load script after browser idle, fallback to immediate load
        if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(addScript);
        } else {
            addScript();
        }

        // Cleanup: remove script when component unmounts or deps change
        return () => {
            const scriptToRemove = document.querySelector('script[src="https://platform.twitter.com/widgets.js"]');
            if (scriptToRemove) {
                scriptToRemove.remove();
            }
        };
    }, [embedKey, showSource]);


    useEffect(() => {
        if (news) {
            console.log("Cita content:", news.cita);
        }
    }, [news]);



    // Debug function to check the format of markdown content
    useEffect(() => {
        if (news?.news) {
            console.log("[NewsDetail] Markdown content sample:", news.news.substring(0, 500));
        }
    }, [news]);


    useEffect(() => {
        if (news?.avail_langs) {
            updateHreflangTags();
        }
    }, [news?.avail_langs, updateHreflangTags]);

    if (loading) return <div className="text-center p-4">Loading...</div>;

    // If news is not found, show a minimal view with the archive button
    if (!news) {
        const { type, subtype } = getTypeAndSubtypeFromPath();
        if (!type) return <div className="text-center p-4">News not found</div>;

        return (
            <div>
                <div className="text-center p-4 mb-8">News not found</div>

                {/* Archive section - Always visible */}
                <div className="max-w-7xl mx-auto px-4">
                    <div className="mt-8 mb-12 border-t border-gray-200 pt-8">
                        <div className="flex justify-center gap-4">
                            <button
                                onClick={() => {
                                    const prefix = getPathPrefix();
                                    let path = `/archive`;
                                    const params = new URLSearchParams();

                                    if (type) {
                                        params.append('type', type.toLowerCase());
                                    }

                                    if (subtype) {
                                        params.append('subtype', subtype);
                                    }

                                    if (params.toString()) {
                                        path = `${path}?${params.toString()}`;
                                    }

                                    navigate(`/${prefix}${path}`);
                                }}
                                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                            >
                                {t?.navigations?.header?.viewArchive || 'View'} {t?.navigations?.header?.archive || 'News Archive'}
                            </button>
                            <a
                                href={`/${getPathPrefix()}/trending`}
                                className="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors duration-200"
                            >
                                {t?.navigations?.header?.worldNews || 'Trending News'}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Title section to display the article type and subtype */}
            <div className="bg-white border-b border-gray-200 py-4 shadow-sm">
                <div className="max-w-7xl mx-auto px-4">
                    <h1 className="text-2xl font-bold capitalize text-gray-800 flex items-center space-x-3">
                        {news.subtype ? (
                            <>
                                <button
                                    onClick={() => {
                                        const prefix = getPathPrefix();
                                        navigate(`/${prefix}/${news.type}`);
                                    }}
                                    className="hover:text-blue-600 transition-colors"
                                >
                                    {getTypeTranslation(news.type)}
                                </button>
                                <span className="text-gray-400">/</span>
                                <button
                                    onClick={() => {
                                        const prefix = getPathPrefix();
                                        const path = `/${prefix}/${news.type}/${news.subtype}`;
                                        console.log('[NewsDetail] Navigating to subtype path:', path);
                                        navigate(path);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    {getSubtypeTranslation(news.subtype)}
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => {
                                    const prefix = getPathPrefix();
                                    navigate(`/${prefix}/${news.type}`);
                                }}
                                className="hover:text-blue-600 transition-colors"
                            >
                                {getTypeTranslation(news.type)}
                            </button>
                        )}
                    </h1>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="lg:flex lg:gap-8">
                    <div className="lg:w-3/4">
                        <div className="text-4xl font-extrabold text-blue-900 mb-6 line-clamp-4 font-serif tracking-tight leading-tight">
                            {processFootnotes(news.title)}
                        </div>
                        <div className="flex justify-between items-center text-gray-600 mb-4">
                            <span>{new Date(news.postTime * 1000).toLocaleString()}</span>

                            

                            <SocialShareButtons lang={lang} newsId={id} newsText={news.news}/>
                        </div>


                        <div className="mb-6">
                            {renderMedia(news)}
                        </div>

                        <div className="prose max-w-none">
                            <ReactMarkdown
                                components={components}
                                remarkPlugins={[remarkGfm]}
                            >
                                {news.news}
                            </ReactMarkdown>
                        </div>

                        {/* Source Section */}
                        <div className="mt-8">
                            <div className="text-blue-800 font-semibold mb-3">Reporter: Global24</div>

                            <button
                                onClick={() => setShowSource(!showSource)}
                                className="text-blue-600 hover:text-blue-800 font-semibold"
                            >
                                {showSource ? "Hide Sources" : "Show Sources"}
                            </button>

                            {showSource && news.cita != null && (
                                <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                                    <h2 className="text-lg font-bold mb-2">Sources</h2>
                                    {news.cita
                                        .replace(/^{|}$/g, "")
                                        .split(/(?=\[\d+\]:)/)
                                        .filter(url => url.trim().length > 0)
                                        .map((source, index) => {
                                            const urlMatch = source.match(/https?:\/\/[^\s\]]+/);
                                            const url = urlMatch ? urlMatch[0] : '';
                                            const numMatch = source.match(/\[(\d+)\]:/);
                                            const num = numMatch ? numMatch[1] : index + 1;

                                            return url ? (
                                                <div key={index} className="mb-3">
                                                    <a
                                                        href={url.trim()}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 break-all block"
                                                    >
                                                        [{num}]: {url.trim()}
                                                    </a>
                                                </div>
                                            ) : null;
                                        })}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="hidden lg:block lg:w-1/4">
                        {/* Space reserved for ads */}
                        <div className="sticky top-4">
                            {/* Ad content will go here */}
                        </div>
                    </div>
                </div>

                {/* Archive section - Always visible at the bottom */}
                <div className="mt-16 mb-12 border-t border-gray-200 pt-8">
                    <div className="flex justify-center gap-4">
                        <button
                            onClick={() => {
                                const prefix = getPathPrefix();
                                let path = `/archive`;
                                const params = new URLSearchParams();

                                if (news.type) {
                                    params.append('type', news.type.toLowerCase());
                                }

                                if (news.subtype) {
                                    params.append('subtype', news.subtype);
                                }

                                if (params.toString()) {
                                    path = `${path}?${params.toString()}`;
                                }

                                navigate(`/${prefix}${path}`);
                            }}
                            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                        >
                            {t?.navigations?.header?.viewArchive || 'View'} {t?.navigations?.header?.archive || 'News Archive'}
                        </button>
                        <a
                            href={`/${getPathPrefix()}/trending`}
                            className="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors duration-200"
                        >
                            {t?.navigations?.header?.worldNews || 'Trending News'}
                        </a>
                    </div>
                </div>

                <div> More Photos</div>

                {news?.img_urls?.[0] && (
                    <TwitterImageCardMeta
                        imageUrl={news.img_urls[0]}
                        title={news.title}
                        description={news.news?.substring(0, 160)}
                    />
                )}
            </div>
        </div>
    );
};

export default NewsDetail;